/**
 * FleetsAdmin - Super Admin page for managing fleet models
 *
 * Features:
 * - View all fleet models with airline assignments
 * - Create new fleet model
 * - Edit fleet model details (code, name, manufacturer)
 * - Delete fleet model
 * - Manage airline-fleet associations
 *
 * Permissions: Super Admin only (Global airline admin)
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../../lib/supabase';
import { useAirline } from '../../contexts/AirlineContext';
import { FleetModel } from '../../lib/types';
import {
  Plus,
  Edit,
  Trash2,
  Truck,
  Building2,
  X,
  Download,
  Loader2,
} from 'lucide-react';

// =====================================================================
// Types
// =====================================================================

interface FleetModelWithStats extends FleetModel {
  airline_count: number;
  doc_count: number;
  airlines?: Array<{
    airline_id: string;
    airline_name: string;
    is_active: boolean;
  }>;
}

interface FleetFormData {
  code: string;
  name: string;
  manufacturer: string;
  description?: string;
  classification?: 'SP' | 'SP HPA' | 'MP' | '';
  is_complex?: boolean;
  is_hpa?: boolean;
}

// =====================================================================
// Validation Schema
// =====================================================================

const fleetSchema = z.object({
  code: z.string().min(2, 'Code must be at least 2 characters').max(20, 'Code too long'),
  name: z.string().min(3, 'Name must be at least 3 characters'),
  manufacturer: z.string().min(2, 'Manufacturer must be at least 2 characters'),
  description: z.string().optional(),
  classification: z.enum(['SP', 'SP HPA', 'MP', '']).optional(),
  is_complex: z.boolean().optional(),
  is_hpa: z.boolean().optional(),
});

type FleetSchemaType = z.infer<typeof fleetSchema>;

// =====================================================================
// Main Component
// =====================================================================

export default function FleetsAdmin() {
  const { isSuperAdmin } = useAirline();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingFleet, setEditingFleet] = useState<FleetModelWithStats | null>(null);
  const [deletingFleet, setDeletingFleet] = useState<FleetModelWithStats | null>(null);
  const [managingAirlines, setManagingAirlines] = useState<FleetModelWithStats | null>(null);
  const [importingEasa, setImportingEasa] = useState(false);

  // ===================================================================
  // Fetch Fleet Models with Stats
  // ===================================================================

  const { data: fleets, isLoading } = useQuery<FleetModelWithStats[]>({
    queryKey: ['fleet-models-admin'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('fleet_models')
        .select(`
          *, classification, is_complex, is_hpa, easa_source, easa_metadata,
          airline_fleets(
            airline_id,
            is_active,
            airlines(name)
          )
        `)
        .order('manufacturer')
        .order('code');

      if (error) throw error;

      // Get document counts separately
      const fleetIds = (data || []).map((f: any) => f.id);
      const { data: docCounts } = await supabase
        .from('doc_library')
        .select('fleet_model_id')
        .in('fleet_model_id', fleetIds);

      const docCountMap = (docCounts || []).reduce((acc: any, doc: any) => {
        acc[doc.fleet_model_id] = (acc[doc.fleet_model_id] || 0) + 1;
        return acc;
      }, {});

      return (data || []).map((fleet: any) => {
        const airlineFleets = fleet.airline_fleets || [];
        const airlines = airlineFleets
          .filter((af: any) => af.airlines)
          .map((af: any) => ({
            airline_id: af.airline_id,
            airline_name: af.airlines.name,
            is_active: af.is_active,
          }));

        return {
          ...fleet,
          airline_count: airlines.length,
          doc_count: docCountMap[fleet.id] || 0,
          airlines,
        };
      });
    },
    enabled: isSuperAdmin,
  });

  // ===================================================================
  // Mutations
  // ===================================================================

  const createMutation = useMutation({
    mutationFn: async (formData: FleetFormData) => {
      const { data, error } = await supabase
        .from('fleet_models')
        .insert({
          code: formData.code,
          name: formData.name,
          manufacturer: formData.manufacturer,
          description: formData.description,
          classification: formData.classification || null,
          is_complex: formData.is_complex ?? false,
          is_hpa: formData.is_hpa ?? false,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet-models-admin'] });
      setShowCreateModal(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, formData }: { id: string; formData: FleetFormData }) => {
      const { data, error } = await supabase
        .from('fleet_models')
        .update({
          code: formData.code,
          name: formData.name,
          manufacturer: formData.manufacturer,
          description: formData.description,
          classification: formData.classification || null,
          is_complex: formData.is_complex ?? false,
          is_hpa: formData.is_hpa ?? false,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet-models-admin'] });
      setEditingFleet(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('fleet_models')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet-models-admin'] });
      setDeletingFleet(null);
    },
  });

  // ===================================================================
  // Permission Check
  // ===================================================================

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You need super admin permissions to access this page.</p>
        </div>
      </div>
    );
  }

  // ===================================================================
  // Render
  // ===================================================================

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Fleet Models Management</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage aircraft fleet models and airline assignments
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={async () => {
              if (!confirm('Import EASA aircraft types?\n\nThis will download the latest EASA type rating PDF, parse it, and add/update fleet models.\nIf EASA is unreachable, bundled data will be used as fallback.')) return;
              setImportingEasa(true);
              try {
                const {
                  data: { session },
                } = await supabase.auth.getSession();
                const accessToken = session?.access_token;
                if (!accessToken) {
                  throw new Error('Authentication required. Please sign in again.');
                }

                const res = await fetch('/api/import-easa-fleet-models', {
                  method: 'POST',
                  headers: {
                    Authorization: `Bearer ${accessToken}`,
                  },
                });
                const result = await res.json();
                if (!res.ok) throw new Error(result.error || 'Import failed');
                const sourceLabel = result.source === 'easa-live' ? 'live EASA PDF' : 'bundled JSON (fallback)';
                let msg = `Imported ${result.count ?? 0} fleet models from ${sourceLabel}.`;
                if (result.warning) msg += `\n\nNote: ${result.warning}`;
                alert(msg);
                queryClient.invalidateQueries({ queryKey: ['fleet-models-admin'] });
              } catch (err: unknown) {
                alert(`EASA import failed: ${err instanceof Error ? err.message : String(err)}`);
              } finally {
                setImportingEasa(false);
              }
            }}
            disabled={importingEasa}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            {importingEasa ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Download size={18} />
                Import EASA Types
              </>
            )}
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={20} />
            Add Fleet Model
          </button>
        </div>
      </div>

      {/* Fleet Models Table */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading fleet models...</div>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-x-auto">
          <table className="w-full table-fixed divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Fleet Model
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Code
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Manufacturer
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                  Class
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                  Airlines
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                  Docs
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {fleets?.map((fleet) => (
                <tr key={fleet.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 max-w-[280px]">
                    <div className="flex items-center gap-2">
                      <Truck className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <div className="min-w-0 overflow-hidden">
                        <div className="text-xs font-medium text-gray-900 truncate">
                          {fleet.name}
                          {fleet.easa_source && (
                            <span className="text-xs text-green-600 bg-green-50 px-1 rounded ml-1">EASA</span>
                          )}
                        </div>
                        {fleet.description && (
                          <div className="text-xs text-gray-500 truncate">{fleet.description}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                      {fleet.code}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-900">
                    {fleet.manufacturer}
                  </td>
                  <td className="px-2 py-2 text-center">
                    {fleet.classification ? (
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                        fleet.classification === 'MP'
                          ? 'bg-blue-100 text-blue-800'
                          : fleet.classification === 'SP HPA'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-gray-100 text-gray-800'
                      }`}>
                        {fleet.classification}
                      </span>
                    ) : (
                      <span className="text-gray-400">&mdash;</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-center">
                    <button
                      onClick={() => setManagingAirlines(fleet)}
                      className="text-xs text-sky-600 hover:text-sky-900 underline"
                    >
                      {fleet.airline_count}
                    </button>
                  </td>
                  <td className="px-2 py-2 text-center text-xs text-gray-900">
                    {fleet.doc_count}
                  </td>
                  <td className="px-2 py-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => setEditingFleet(fleet)}
                        className="p-1 text-sky-600 hover:bg-sky-50 rounded"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => setDeletingFleet(fleet)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <FleetFormModal
          title="Create New Fleet Model"
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          isLoading={createMutation.isPending}
          error={createMutation.error as Error | null}
        />
      )}

      {/* Edit Modal */}
      {editingFleet && (
        <FleetFormModal
          title="Edit Fleet Model"
          fleet={editingFleet}
          onClose={() => setEditingFleet(null)}
          onSubmit={(data) => updateMutation.mutate({ id: editingFleet.id, formData: data })}
          isLoading={updateMutation.isPending}
          error={updateMutation.error as Error | null}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingFleet && (
        <DeleteConfirmationModal
          fleet={deletingFleet}
          onClose={() => setDeletingFleet(null)}
          onConfirm={() => deleteMutation.mutate(deletingFleet.id)}
          isLoading={deleteMutation.isPending}
          error={deleteMutation.error as Error | null}
        />
      )}

      {/* Airline Assignments Modal */}
      {managingAirlines && (
        <AirlineAssignmentsModal
          fleet={managingAirlines}
          onClose={() => setManagingAirlines(null)}
        />
      )}
    </div>
  );
}

// =====================================================================
// Fleet Form Modal
// =====================================================================

interface FleetFormModalProps {
  title: string;
  fleet?: FleetModelWithStats;
  onClose: () => void;
  onSubmit: (data: FleetFormData) => void;
  isLoading: boolean;
  error: Error | null;
}

const FleetFormModal: React.FC<FleetFormModalProps> = ({
  title,
  fleet,
  onClose,
  onSubmit,
  isLoading,
  error,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FleetSchemaType>({
    resolver: zodResolver(fleetSchema),
    defaultValues: {
      code: fleet?.code || '',
      name: fleet?.name || '',
      manufacturer: fleet?.manufacturer || '',
      description: fleet?.description || '',
      classification: fleet?.classification || '',
      is_complex: fleet?.is_complex ?? false,
      is_hpa: fleet?.is_hpa ?? false,
    },
  });

  const handleFormSubmit = (data: FleetSchemaType) => {
    onSubmit(data);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
            disabled={isLoading}
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(handleFormSubmit)} className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded">
              <p className="font-medium">Error</p>
              <p className="text-sm">{error.message}</p>
            </div>
          )}

          <div className="space-y-4">
            {/* Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fleet Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('code')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                placeholder="A320"
              />
              {errors.code && (
                <p className="mt-1 text-sm text-red-600">{errors.code.message}</p>
              )}
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fleet Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('name')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                placeholder="Airbus A320"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            {/* Manufacturer */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Manufacturer <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('manufacturer')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                placeholder="Airbus"
              />
              {errors.manufacturer && (
                <p className="mt-1 text-sm text-red-600">{errors.manufacturer.message}</p>
              )}
            </div>

            {/* Description (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                {...register('description')}
                rows={3}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                placeholder="Narrow-body jet airliner"
              />
            </div>

            {/* Classification */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Classification
              </label>
              <select
                {...register('classification')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500"
              >
                <option value="">-- None --</option>
                <option value="SP">SP (Single Pilot)</option>
                <option value="SP HPA">SP HPA (Single Pilot, High Performance)</option>
                <option value="MP">MP (Multi Pilot)</option>
              </select>
            </div>

            {/* Checkboxes */}
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  {...register('is_complex')}
                  className="rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                />
                Complex aircraft
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  {...register('is_hpa')}
                  className="rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                />
                High Performance Aircraft (HPA)
              </label>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit(handleFormSubmit)}
            disabled={isLoading}
            className="btn-primary disabled:opacity-50"
          >
            {isLoading ? 'Saving...' : fleet ? 'Update Fleet' : 'Create Fleet'}
          </button>
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// Delete Confirmation Modal
// =====================================================================

interface DeleteConfirmationModalProps {
  fleet: FleetModelWithStats;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  error: Error | null;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  fleet,
  onClose,
  onConfirm,
  isLoading,
  error,
}) => {
  const hasResources = fleet.airline_count > 0 || fleet.doc_count > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Delete Fleet Model?</h2>

        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded">
            <p className="font-medium">Error</p>
            <p className="text-sm">{error.message}</p>
          </div>
        )}

        <p className="text-gray-600 mb-4">
          Are you sure you want to delete <strong>{fleet.name} ({fleet.code})</strong>?
        </p>

        {hasResources && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            <p className="font-medium">Warning</p>
            <p className="text-sm">
              This fleet model has {fleet.airline_count} airline assignment(s) and {fleet.doc_count}{' '}
              document(s). Deleting will cascade to all related records.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {isLoading ? 'Deleting...' : 'Delete Fleet'}
          </button>
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// Airline Assignments Modal
// =====================================================================

interface AirlineAssignmentsModalProps {
  fleet: FleetModelWithStats;
  onClose: () => void;
}

const AirlineAssignmentsModal: React.FC<AirlineAssignmentsModalProps> = ({ fleet, onClose }) => {
  const queryClient = useQueryClient();
  const [toggleError, setToggleError] = useState<string | null>(null);

  // Fetch all airlines
  const { data: allAirlines } = useQuery({
    queryKey: ['airlines-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('airlines')
        .select('id, name, code')
        .order('name');

      if (error) throw error;
      return data;
    },
  });

  // Live query for current assignments (not stale prop)
  const { data: assignments } = useQuery({
    queryKey: ['airline-fleet-assignments', fleet.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('airline_fleets')
        .select('airline_id, is_active')
        .eq('fleet_model_id', fleet.id);

      if (error) throw error;
      return data || [];
    },
  });

  // Toggle airline-fleet assignment
  const toggleMutation = useMutation({
    mutationFn: async ({ airlineId, isActive }: { airlineId: string; isActive: boolean }) => {
      if (isActive) {
        // Remove assignment
        const { error } = await supabase
          .from('airline_fleets')
          .delete()
          .eq('airline_id', airlineId)
          .eq('fleet_model_id', fleet.id);

        if (error) throw error;
      } else {
        // Add assignment
        const { error } = await supabase
          .from('airline_fleets')
          .insert({
            airline_id: airlineId,
            fleet_model_id: fleet.id,
            is_active: true,
          });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      setToggleError(null);
      queryClient.invalidateQueries({ queryKey: ['airline-fleet-assignments', fleet.id] });
      queryClient.invalidateQueries({ queryKey: ['fleet-models-admin'] });
    },
    onError: (err: Error) => {
      setToggleError(err.message || 'Failed to update assignment. Check admin permissions.');
    },
  });

  const isAssigned = (airlineId: string) => {
    return assignments?.some((a) => a.airline_id === airlineId && a.is_active) || false;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            Airline Assignments: {fleet.name}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X size={24} />
          </button>
        </div>

        {/* Airlines List */}
        <div className="flex-1 overflow-y-auto p-6">
          {toggleError && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded">
              <p className="text-sm">{toggleError}</p>
            </div>
          )}
          <div className="space-y-2">
            {allAirlines?.map((airline) => {
              const assigned = isAssigned(airline.id);
              return (
                <div
                  key={airline.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-gray-400" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{airline.name}</div>
                      <div className="text-xs text-gray-500">{airline.code}</div>
                    </div>
                  </div>

                  <button
                    onClick={() =>
                      toggleMutation.mutate({ airlineId: airline.id, isActive: assigned })
                    }
                    disabled={toggleMutation.isPending}
                    className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                      assigned
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-sky-100 text-sky-700 hover:bg-sky-200'
                    } disabled:opacity-50`}
                  >
                    {assigned ? 'Remove' : 'Add'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button onClick={onClose} className="btn-primary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
